import { format, startOfMonth, subDays, subMonths } from 'date-fns';

import { liveOptionsFor, type FilterOption, type FilterSchema } from '@/lib/filters';

/** Before this day of the month too little is recorded, so the report opens on last month instead. */
const MONTH_OPENS_ON = 10;

const ISO = 'yyyy-MM-dd';

/** Period the report opens on when the URL carries none. To = yesterday; today is still being recorded. */
export function defaultPeriod(today: Date = new Date()): { from: string; to: string } {
  const from =
    today.getDate() >= MONTH_OPENS_ON ? startOfMonth(today) : startOfMonth(subMonths(today, 1));
  return { from: format(from, ISO), to: format(subDays(today, 1), ISO) };
}

/** Option values come from `report.yaml`'s `options:` block, never a hardcoded array (§5.4). */
const live = liveOptionsFor('production-monthly-output');

/** Legacy GET_FACTORY_WITH_PERMISSIONS; a fixed list here until a dict endpoint exists. */
export const FACTORY_OPTIONS: readonly FilterOption[] = [
  { value: 'BCA', label: 'BCA' },
  { value: 'BVG', label: 'BVG' },
  { value: 'BVN', label: 'BVN' },
  { value: 'BYS', label: 'BYS' },
  { value: 'PGV', label: 'PGV' },
];

const FACTORY_CODES = new Set(FACTORY_OPTIONS.map((o) => o.value));

/** Login default factory from Base Portal; set by AuthProvider when the session lands. */
let sessionLoginFactory: string | null = null;

/** Called when the verified user is known (or cleared on sign-out). */
export function setSessionLoginFactory(factory: string | null | undefined): void {
  sessionLoginFactory =
    factory === null || factory === undefined || factory.trim() === ''
      ? null
      : factory.trim().toUpperCase();
}

/** Factory to open on when the URL has none — login site if known, else the first real factory. */
export function preferredFactory(loginFactory?: string | null): string {
  const raw = (loginFactory ?? sessionLoginFactory ?? '').trim().toUpperCase();
  if (raw !== '' && FACTORY_CODES.has(raw)) {
    return raw;
  }
  return FACTORY_OPTIONS[0]!.value;
}

/**
 * Mirrors the `filters:` block of `production-monthly-output/report.yaml`.
 *
 * Explicit "All factories" writes `factory=__any__` so the login default cannot overwrite it on the
 * next parse. Brand multi-select: nothing checked = all brands (`:brand_any = 1`).
 */
export const productionOutputFilters: FilterSchema = [
  {
    type: 'select',
    name: 'factory',
    label: 'Factory',
    anyLabel: 'All factories (by date)',
    // Resolved on each parse so a late-arriving login default is used (not frozen at module load).
    defaultValue: () => preferredFactory(),
    options: FACTORY_OPTIONS,
  },
  {
    type: 'date-range',
    name: 'period',
    label: 'Period',
    required: true,
    fromParam: 'from',
    toParam: 'to',
    defaultValue: defaultPeriod,
  },
  {
    type: 'multi-select',
    name: 'brand',
    label: 'Brand',
    anyLabel: 'All brands',
    options: live('brand'),
  },
];
